import React, {useEffect, useMemo} from 'react'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {FixedMessenger, ManualSessionInfo} from '@sendbird/ai-agent-messenger-react'

import {SENDBIRD_CONFIG} from '@/constants/sendbird'
import {IMainLayoutStore} from '@/layouts/MainLayout/IMainLayoutStore'
import {MainLayoutStoreSymbol} from '@/layouts/MainLayout/MainLayout'
import {ProfileStoreInterface, ProfileStoreSymbol} from '@/pages/Profile'
import {useAiChatMessengerStore} from '@/stores/ai-chat-messenger/hooks/use-ai-chat-messenger-store'
import {AuthStoreInterface, AuthStoreSymbol} from '@/stores'
import {storage} from '@/services/storage'
import {ACCESS_TOKEN_KEY} from '@/constants/auth-constants'
import {LoadingState} from '@/types'

export const AIChatMessenger = observer(() => {
    const store = useInjection<IMainLayoutStore>(MainLayoutStoreSymbol)
    const profileStore = useInjection<ProfileStoreInterface>(ProfileStoreSymbol)
    const authStore = useInjection<AuthStoreInterface>(AuthStoreSymbol)
    const aiChatStore = useAiChatMessengerStore()
    const {merchantCompanyInfo} = store

    useEffect(() => {
        if (profileStore?.profileData?.publicId) {
            aiChatStore.initializeAuth().catch((error) => {
                console.error('Failed to initialize AI chat authentication:', error)
            })
        }
    }, [aiChatStore, profileStore?.profileData?.publicId])

    const chatContext = useMemo(() => {
        if (!store.merchant?.merchantId || !store.profileData?.publicId || !authStore.email) {
            return null
        }

        return {
            merchant_id: store.merchant.merchantId,
            user_name: store.profileData.firstName
                ? `${store.profileData.firstName} ${store.profileData?.lastName || ''}`.trim()
                : '',
            email: authStore.email,
            platform: 'web',
            user_id: store.profileData?.publicId,
            access_token: authStore?.authResponse?.access_token || storage.get(ACCESS_TOKEN_KEY),
            // Salesforce case fields
            account: merchantCompanyInfo?.salesForceAccountId || '',
            phone: merchantCompanyInfo?.companyPhoneNumber || '',
            companyName: merchantCompanyInfo?.companyName || '',
            companyNumber: merchantCompanyInfo?.companyNumber || '',
            dsrId: merchantCompanyInfo?.dsrId ? String(merchantCompanyInfo.dsrId) : '',
            market: merchantCompanyInfo?.marketCode || '',
            tenant: merchantCompanyInfo?.tenantValue || '',
            merchantSegment: merchantCompanyInfo?.segmentCategoryValue || ''
        }
    }, [
        store.merchant?.merchantId,
        store?.profileData?.publicId,
        store?.profileData?.firstName,
        store?.profileData?.lastName,
        authStore.email,
        merchantCompanyInfo
    ])

    const sessionInfo = useMemo(() => {
        if (!profileStore?.profileData?.publicId || !aiChatStore.authToken) {
            return null
        }

        return new ManualSessionInfo({
            userId: profileStore.profileData.publicId,
            authToken: aiChatStore.authToken,
            sessionHandler: {
                onSessionTokenRequired: async () => {},
                onSessionClosed: () => {},
                onSessionError: () => {},
                onSessionRefreshed: () => {}
            }
        })
    }, [profileStore?.profileData?.publicId, aiChatStore.authToken, aiChatStore])

    if (!store.merchant?.merchantId || !profileStore?.profileData?.publicId || !chatContext) {
        return null
    }

    if (
        aiChatStore.loadingState === LoadingState.LOADING ||
        aiChatStore.loadingState === LoadingState.FAILED
    ) {
        return null
    }

    return (
        <FixedMessenger
            appId={SENDBIRD_CONFIG.APP_ID}
            aiAgentId={SENDBIRD_CONFIG.AI_AGENT_ID}
            language={SENDBIRD_CONFIG.DEFAULT_LANGUAGE}
            countryCode={SENDBIRD_CONFIG.DEFAULT_COUNTRY_CODE}
            context={chatContext}
            userSessionInfo={sessionInfo}
        />
    )
})
